import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Container, Box, Typography, Paper, Tabs, Tab, Switch, FormControlLabel,
  Checkbox, Button, Stack, Divider, Alert, Chip, Avatar, CircularProgress,
  Snackbar, TextField, InputAdornment, IconButton, Tooltip
} from '@mui/material';
import { User, sendPasswordResetEmail, sendEmailVerification } from 'firebase/auth';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { useRoster } from '../RosterContext';
import { getBackendUrl, getBackendEnv } from '../config';
import { UserNotificationPreferences } from '../types';
import {
  fetchUserPreferences,
  updateUserPreferences,
  sendNotificationPreview,
  resetCalendarFeedToken,
} from '../utils/apiClient';

export default function SettingsPage() {
  const { user } = useOutletContext<{ user: User | null; isTeamManager: boolean }>();
  const { userProfile } = useRoster();

  const [activeTab, setActiveTab] = useState<number>(0);
  const [preferences, setPreferences] = useState<UserNotificationPreferences | null>(null);
  const [initialPreferences, setInitialPreferences] = useState<UserNotificationPreferences | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [previewSending, setPreviewSending] = useState<'daily' | 'weekly' | null>(null);
  const [isResettingToken, setIsResettingToken] = useState<boolean>(false);
  const [confirmResetToken, setConfirmResetToken] = useState<boolean>(false);

  // Account & Security states
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [resetMessage, setResetMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Snackbar notifications
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setIsLoading(true);
        const data = await fetchUserPreferences();
        if (mounted) {
          setPreferences(data);
          setInitialPreferences(JSON.parse(JSON.stringify(data)));
        }
      } catch (err: any) {
        if (mounted) {
          setSnackbar({
            open: true,
            message: err.message || 'Failed to load user preferences.',
            severity: 'error',
          });
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }
    if (user) {
      load();
    }
  }, [user]);

  const hasUnsavedChanges = Boolean(
    preferences &&
    initialPreferences &&
    JSON.stringify(preferences) !== JSON.stringify(initialPreferences)
  );

  const handleEventTypeChange = (key: keyof UserNotificationPreferences['daily']['eventTypes'], checked: boolean) => {
    if (!preferences) return;
    const currentEvents = preferences.eventTypes || preferences.daily.eventTypes;
    const updatedEvents = {
      ...currentEvents,
      [key]: checked,
    };
    setPreferences({
      ...preferences,
      eventTypes: updatedEvents,
      daily: {
        ...preferences.daily,
        eventTypes: updatedEvents,
      },
    });
  };

  const handleSavePreferences = async () => {
    if (!preferences) return;
    setIsSaving(true);
    try {
      const res = await updateUserPreferences(preferences);
      setPreferences(res.preferences);
      setInitialPreferences(JSON.parse(JSON.stringify(res.preferences)));
      setSnackbar({
        open: true,
        message: 'Notification preferences saved successfully!',
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to save preferences.',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendPreview = async (type: 'daily' | 'weekly') => {
    setPreviewSending(type);
    try {
      const res = await sendNotificationPreview(type);
      setSnackbar({
        open: true,
        message: res.message || `Sample ${type} email sent! Check your inbox.`,
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || `Failed to send ${type} preview email.`,
        severity: 'error',
      });
    } finally {
      setPreviewSending(null);
    }
  };

  const getCalendarFeedBaseUrl = () => {
    if (
      typeof window !== 'undefined' &&
      getBackendEnv() === 'prod' &&
      (window.location.hostname === 'jets14.com' || window.location.hostname === 'www.jets14.com')
    ) {
      return window.location.origin.replace(/\/$/, '');
    }
    return (
      getBackendUrl() ||
      (typeof window !== 'undefined' ? window.location.origin : 'https://jets14.com')
    ).replace(/\/$/, '');
  };

  const handleCopyFeedUrl = async (feedUrl: string) => {
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(feedUrl);
      } else {
        const temp = document.createElement('textarea');
        temp.value = feedUrl;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);
      }
      setSnackbar({
        open: true,
        message: 'Calendar feed URL copied to clipboard!',
        severity: 'success',
      });
    } catch {
      setSnackbar({
        open: true,
        message: 'Could not copy automatically — please select and copy the URL manually.',
        severity: 'info',
      });
    }
  };

  const handleResetCalendarToken = async () => {
    if (!preferences) return;
    setIsResettingToken(true);
    try {
      const res = await resetCalendarFeedToken();
      const newMeta = {
        ...(preferences.metadata || {}),
        ...(res.preferences?.metadata || {}),
        calendarFeedToken: res.calendarFeedToken,
        calendarFeedEnabled: true,
      };
      setPreferences(prev => (prev ? { ...prev, metadata: newMeta } : prev));
      setInitialPreferences(prev => (prev ? { ...prev, metadata: JSON.parse(JSON.stringify(newMeta)) } : prev));
      setConfirmResetToken(false);
      setSnackbar({
        open: true,
        message: 'Calendar subscription link reset. Previous URL has been revoked.',
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to reset calendar subscription link.',
        severity: 'error',
      });
    } finally {
      setIsResettingToken(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!user || !user.email) return;
    setIsSendingReset(true);
    setResetMessage(null);
    try {
      const authInstance = (user as any).auth || undefined;
      await sendPasswordResetEmail(authInstance || (user as any).auth, user.email);
      setResetMessage({
        type: 'success',
        text: `A password reset link has been sent to ${user.email}. Please check your inbox.`,
      });
    } catch (error: any) {
      setResetMessage({
        type: 'error',
        text: error.message || 'Failed to send password reset email.',
      });
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleResendVerification = async () => {
    if (!user) return;
    setIsSendingVerification(true);
    setVerificationMessage(null);
    try {
      await sendEmailVerification(user);
      setVerificationMessage({
        type: 'success',
        text: `Verification email sent to ${user.email}! Please click the link in your email.`,
      });
    } catch (error: any) {
      setVerificationMessage({
        type: 'error',
        text: error.message || 'Failed to send verification email.',
      });
    } finally {
      setIsSendingVerification(false);
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Page Title */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={800} color="#0b2341" gutterBottom>
          Settings & Preferences
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Personalize your automated email notifications and manage your account security.
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, mb: 4, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          sx={{
            borderBottom: '1px solid #e2e8f0',
            bgcolor: '#f8fafc',
            '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', fontSize: '0.95rem', py: 1.8 },
          }}
        >
          <Tab icon={<NotificationsActiveIcon />} iconPosition="start" label="Notifications & Email" />
          <Tab icon={<ManageAccountsIcon />} iconPosition="start" label="Account & Security" />
        </Tabs>

        {/* TAB 0: NOTIFICATIONS & EMAIL PREFERENCES */}
        {activeTab === 0 && preferences && (
          <Box sx={{ p: { xs: 2.5, sm: 4 } }}>
            <Stack spacing={4}>
              {/* CARD 1: WEEKLY UPDATE EMAILS */}
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 2,
                  borderColor: preferences.weekly.enabled ? '#0b2341' : '#e2e8f0',
                  bgcolor: preferences.weekly.enabled ? '#fafcff' : '#ffffff',
                  transition: 'all 0.2s ease',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="h6" fontWeight={700} color="#0b2341">
                        Weekly Team Update
                      </Typography>
                      <Chip label="weekly@jets14.com" size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Sent every Monday morning at 7:00 AM Central Time.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.weekly.enabled}
                        onChange={(e) => {
                          const val = e.target.checked;
                          setPreferences({
                            ...preferences,
                            weekly: {
                              ...preferences.weekly,
                              enabled: val,
                              // If enabling for the first time, check all sub-options for a good initial experience
                              includeUpcomingSchedule: val ? true : preferences.weekly.includeUpcomingSchedule,
                              includeStandingsOverview: val ? true : preferences.weekly.includeStandingsOverview,
                              includeLeagueOutcomes: val ? true : preferences.weekly.includeLeagueOutcomes,
                            },
                          });
                        }}
                        color="primary"
                      />
                    }
                    label={preferences.weekly.enabled ? 'Enabled' : 'Disabled'}
                    labelPlacement="start"
                    sx={{ m: 0 }}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ opacity: preferences.weekly.enabled ? 1 : 0.5, pointerEvents: preferences.weekly.enabled ? 'auto' : 'none' }}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 1.5 }}>
                    Include in weekly updates:
                  </Typography>

                  <Stack spacing={1} sx={{ pl: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={preferences.weekly.includeUpcomingSchedule}
                          onChange={(e) =>
                            setPreferences({
                              ...preferences,
                              weekly: { ...preferences.weekly, includeUpcomingSchedule: e.target.checked },
                            })
                          }
                          size="small"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>Upcoming Week Schedule</Typography>
                          <Typography variant="caption" color="text.secondary">Practices, games, tournaments, rinks & ice times for the week ahead</Typography>
                        </Box>
                      }
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={preferences.weekly.includeStandingsOverview}
                          onChange={(e) =>
                            setPreferences({
                              ...preferences,
                              weekly: { ...preferences.weekly, includeStandingsOverview: e.target.checked },
                            })
                          }
                          size="small"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>Team Standings & Projections</Typography>
                          <Typography variant="caption" color="text.secondary">Current CSDHL rank, record, points, and Monte Carlo end-of-season projection</Typography>
                        </Box>
                      }
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={preferences.weekly.includeLeagueOutcomes}
                          onChange={(e) =>
                            setPreferences({
                              ...preferences,
                              weekly: { ...preferences.weekly, includeLeagueOutcomes: e.target.checked },
                            })
                          }
                          size="small"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>League Game Outcomes</Typography>
                          <Typography variant="caption" color="text.secondary">Summary of recent game scores and results across CSDHL divisions</Typography>
                        </Box>
                      }
                    />
                  </Stack>

                  <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px dashed #e2e8f0' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={previewSending === 'weekly' ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                      onClick={() => handleSendPreview('weekly')}
                      disabled={Boolean(previewSending)}
                    >
                      Send Sample Weekly Email
                    </Button>
                  </Box>
                </Box>
              </Paper>

              {/* CARD 2: DAILY EVENT REMINDERS */}
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 2,
                  borderColor: preferences.daily.enabled ? '#0b2341' : '#e2e8f0',
                  bgcolor: preferences.daily.enabled ? '#fafcff' : '#ffffff',
                  transition: 'all 0.2s ease',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="h6" fontWeight={700} color="#0b2341">
                        Daily Event Reminders
                      </Typography>
                      <Chip label="reminders@jets14.com" size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Sent at 6:30 AM Central Time on days when you have scheduled events.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.daily.enabled}
                        onChange={(e) => {
                          const val = e.target.checked;
                          const currentEvents = preferences.eventTypes || preferences.daily.eventTypes;
                          const initialEvents = val && !currentEvents.games && !currentEvents.practices ? {
                            ...currentEvents,
                            games: true,
                            practices: true,
                          } : currentEvents;

                          setPreferences({
                            ...preferences,
                            eventTypes: initialEvents,
                            daily: {
                              ...preferences.daily,
                              enabled: val,
                              eventTypes: initialEvents,
                            },
                          });
                        }}
                        color="primary"
                      />
                    }
                    label={preferences.daily.enabled ? 'Enabled' : 'Disabled'}
                    labelPlacement="start"
                    sx={{ m: 0 }}
                  />
                </Box>

                <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ my: 2, borderRadius: 1.5 }}>
                  No emails will be sent on off-days or days without matching events.
                </Alert>

                <Box sx={{ opacity: preferences.daily.enabled ? 1 : 0.5, pointerEvents: preferences.daily.enabled ? 'auto' : 'none' }}>
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed #e2e8f0' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={previewSending === 'daily' ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                      onClick={() => handleSendPreview('daily')}
                      disabled={Boolean(previewSending)}
                    >
                      Send Sample Daily Reminder
                    </Button>
                  </Box>
                </Box>
              </Paper>

              {/* CARD 3: EVENT CATEGORIES TO INCLUDE (SHARED) */}
              {(() => {
                const currentEvents = preferences.eventTypes || preferences.daily.eventTypes;

                return (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      borderColor: '#e2e8f0',
                      bgcolor: '#ffffff',
                    }}
                  >
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="h6" fontWeight={700} color="#0b2341">
                          Event Categories to Include
                        </Typography>
                        <Chip label="Shared Schedule Preferences" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.72rem' }} />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Choose which event types to include in your morning daily reminders, weekly upcoming schedule, and personal calendar subscription (.ics feed).
                      </Typography>
                    </Box>

                    <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ mb: 2.5, borderRadius: 1.5 }}>
                      Families who do not play goalie can uncheck <strong>Goalie Ice &amp; Clinics</strong> to exclude them from morning reminders, the weekly schedule recap, and your personal calendar feed.
                    </Alert>

                    <Box>
                      <Stack spacing={1.5} sx={{ pl: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(currentEvents.games)}
                              onChange={(e) => handleEventTypeChange('games', e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={600}>Games</Typography>
                              <Typography variant="caption" color="text.secondary">CSDHL regular season games and exhibition matchups</Typography>
                            </Box>
                          }
                        />

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(currentEvents.practices)}
                              onChange={(e) => handleEventTypeChange('practices', e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={600}>Practices &amp; Skills</Typography>
                              <Typography variant="caption" color="text.secondary">Regular team practices, skills sessions, and Jets Academy ice</Typography>
                            </Box>
                          }
                        />

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(currentEvents.goalie)}
                              onChange={(e) => handleEventTypeChange('goalie', e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={600}>Goalie Ice &amp; Clinics</Typography>
                              <Typography variant="caption" color="text.secondary">Dedicated goalie coaching and clinic sessions (Recommended for goalie families)</Typography>
                            </Box>
                          }
                        />

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(currentEvents.birthdays)}
                              onChange={(e) => handleEventTypeChange('birthdays', e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={600}>Player Birthdays</Typography>
                              <Typography variant="caption" color="text.secondary">Team member birthday celebrations (Private — only included when opted in)</Typography>
                            </Box>
                          }
                        />

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(currentEvents.tournaments)}
                              onChange={(e) => handleEventTypeChange('tournaments', e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={600}>Tournaments &amp; Special Events</Typography>
                              <Typography variant="caption" color="text.secondary">Weekend tournament games and special team events</Typography>
                            </Box>
                          }
                        />

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(currentEvents.dryland)}
                              onChange={(e) => handleEventTypeChange('dryland', e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={600}>Off-Ice &amp; GVN Performance</Typography>
                              <Typography variant="caption" color="text.secondary">Dryland training, GVN, and floorball sessions</Typography>
                            </Box>
                          }
                        />
                      </Stack>
                    </Box>
                  </Paper>
                );
              })()}

              {/* CARD 4: PERSONAL CALENDAR SUBSCRIPTION (.ICS FEED) */}
              {(() => {
                const calendarToken = preferences.metadata?.calendarFeedToken;
                const feedBaseUrl = getCalendarFeedBaseUrl();
                const feedUrl = calendarToken
                  ? `${feedBaseUrl}/api/calendar.ics?key=${encodeURIComponent(calendarToken)}`
                  : '';
                const webcalUrl = feedUrl.replace(/^https?:\/\//i, 'webcal://');

                return (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      borderColor: '#0b2341',
                      bgcolor: '#fafcff',
                    }}
                  >
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <CalendarMonthIcon sx={{ color: '#0b2341', fontSize: 22 }} />
                        <Typography variant="h6" fontWeight={700} color="#0b2341">
                          Personal Calendar Subscription (.ics Feed)
                        </Typography>
                        <Chip
                          label="Apple • Google • Outlook"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontSize: '0.72rem' }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Subscribe once in Apple Calendar, Google Calendar, or Outlook to automatically sync Jets &apos;14 games, practices, tournament schedules, canonical rink locations, and your family&apos;s volunteer duties.
                      </Typography>
                    </Box>

                    <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ mb: 2.5, borderRadius: 1.5 }}>
                      This private subscription URL is unique to your account. It filters events using your <strong>Event Categories</strong> above, highlights your family&apos;s assigned volunteer duties (e.g., <code>[Duty: Scoreboard]</code>), and protects player birthdays behind your secret key.
                    </Alert>

                    {calendarToken ? (
                      <Stack spacing={2}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Personal Calendar Feed URL"
                          value={feedUrl}
                          InputProps={{
                            readOnly: true,
                            sx: {
                              bgcolor: '#ffffff',
                              fontFamily: 'monospace',
                              fontSize: '0.82rem',
                            },
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title="Copy Feed URL">
                                  <IconButton
                                    size="small"
                                    aria-label="Copy calendar feed URL"
                                    onClick={() => handleCopyFeedUrl(feedUrl)}
                                    edge="end"
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                          inputProps={{
                            'aria-label': 'Personal Calendar Feed URL',
                            onFocus: (e: React.FocusEvent<HTMLInputElement>) => e.target.select(),
                          }}
                        />

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<ContentCopyIcon fontSize="small" />}
                              onClick={() => handleCopyFeedUrl(feedUrl)}
                              disableElevation
                            >
                              Copy Feed URL
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              component="a"
                              href={webcalUrl}
                              startIcon={<OpenInNewIcon fontSize="small" />}
                            >
                              Subscribe in Calendar
                            </Button>
                          </Stack>

                          {!confirmResetToken ? (
                            <Button
                              variant="text"
                              color="warning"
                              size="small"
                              startIcon={<AutorenewIcon fontSize="small" />}
                              onClick={() => setConfirmResetToken(true)}
                              disabled={isResettingToken}
                            >
                              Reset / Revoke Link
                            </Button>
                          ) : (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                startIcon={isResettingToken ? <CircularProgress size={14} color="inherit" /> : <AutorenewIcon fontSize="small" />}
                                onClick={handleResetCalendarToken}
                                disabled={isResettingToken}
                                disableElevation
                              >
                                {isResettingToken ? 'Resetting...' : 'Confirm Reset'}
                              </Button>
                              <Button
                                variant="text"
                                color="inherit"
                                size="small"
                                onClick={() => setConfirmResetToken(false)}
                                disabled={isResettingToken}
                              >
                                Cancel
                              </Button>
                            </Stack>
                          )}
                        </Box>

                        {confirmResetToken && (
                          <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
                            Resetting generates a new secret URL and immediately revokes your current link. Any calendar apps subscribed to the old URL will stop updating until you subscribe with the new link.
                          </Alert>
                        )}
                      </Stack>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary">
                          Generate your personal calendar subscription link to sync events with your calendar app.
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={isResettingToken ? <CircularProgress size={14} /> : <AutorenewIcon fontSize="small" />}
                          onClick={handleResetCalendarToken}
                          disabled={isResettingToken}
                        >
                          Generate Subscription Link
                        </Button>
                      </Box>
                    )}
                  </Paper>
                );
              })()}


              {/* SAVE BUTTON BAR */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 1 }}>
                {hasUnsavedChanges && (
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={() => {
                      if (initialPreferences) {
                        setPreferences(JSON.parse(JSON.stringify(initialPreferences)));
                      }
                    }}
                    disabled={isSaving}
                  >
                    Discard Changes
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  onClick={handleSavePreferences}
                  disabled={isSaving || !hasUnsavedChanges}
                  sx={{ px: 4, py: 1.2, fontWeight: 700 }}
                  disableElevation
                >
                  {isSaving ? 'Saving...' : 'Save Preferences'}
                </Button>
              </Box>
            </Stack>
          </Box>
        )}

        {/* TAB 1: ACCOUNT & SECURITY (MIGRATED FROM DIALOG) */}
        {activeTab === 1 && user && (
          <Box sx={{ p: { xs: 2.5, sm: 4 } }}>
            <Stack spacing={4}>
              {/* Profile Card */}
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={700} color="#0b2341" sx={{ mb: 2 }}>
                  Account Profile
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar
                    src={user.photoURL || undefined}
                    sx={{ width: 64, height: 64, bgcolor: '#0b2341', fontSize: '1.75rem', fontWeight: 700 }}
                  >
                    {!user.photoURL && user.email ? user.email.charAt(0).toUpperCase() : ''}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      {user.displayName || user.email?.split('@')[0]}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {userProfile?.isTeamManager && (
                        <Chip label="Team Manager" color="info" size="small" sx={{ mr: 0.5 }} />
                      )}
                      {userProfile?.isAdmin && (
                        <Chip label="Admin" color="primary" size="small" />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Paper>

              {/* Email Verification Card */}
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={700} color="#0b2341" sx={{ mb: 1 }}>
                  Email Verification
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  {user.emailVerified ? (
                    <Chip icon={<CheckCircleIcon />} label="Email Verified" color="success" size="small" />
                  ) : (
                    <Chip icon={<WarningAmberIcon />} label="Email Not Verified" color="warning" size="small" />
                  )}
                </Stack>

                {!user.emailVerified && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Your email address is unverified. Verify your email to ensure timely notifications and uninterrupted account access.
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={isSendingVerification ? <CircularProgress size={16} /> : <MarkEmailReadIcon />}
                      onClick={handleResendVerification}
                      disabled={isSendingVerification}
                    >
                      Resend Verification Email
                    </Button>
                  </Box>
                )}

                {verificationMessage && (
                  <Alert severity={verificationMessage.type} sx={{ mt: 2 }}>
                    {verificationMessage.text}
                  </Alert>
                )}
              </Paper>

              {/* Password & Security Card */}
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={700} color="#0b2341" sx={{ mb: 1 }}>
                  Password &amp; Security
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  To change your password, request a secure reset link sent directly to your registered email address.
                </Typography>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={isSendingReset ? <CircularProgress size={16} /> : <LockResetIcon />}
                  onClick={handlePasswordReset}
                  disabled={isSendingReset}
                >
                  Send Password Reset Link
                </Button>

                {resetMessage && (
                  <Alert severity={resetMessage.type} sx={{ mt: 2 }}>
                    {resetMessage.text}
                  </Alert>
                )}
              </Paper>

              {/* Associated Players */}
              {userProfile?.associatedPlayers && userProfile.associatedPlayers.length > 0 && (
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight={700} color="#0b2341" sx={{ mb: 1.5 }}>
                    Associated Players
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {userProfile.associatedPlayers.map((p, idx) => (
                      <Chip
                        key={idx}
                        label={`${p.jersey ? '#' + p.jersey + ' ' : ''}${p.firstName} ${p.lastName} (${p.seasonKey})`}
                        variant="outlined"
                        color="primary"
                        sx={{ fontWeight: 600 }}
                      />
                    ))}
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Global Snackbar Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
